<template>
    <v-dialog v-model="dialog" persistent max-width="800">
        <v-card>
            <v-card-title class="text-h5 pa-6">
                <v-icon class="mr-3" color="primary">mdi-calendar-edit</v-icon>
                Edit Meeting
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pa-6">
                <v-form ref="editMeetingForm" v-model="formValid">
                    <v-row>
                        <!-- Title -->
                        <v-col cols="12">
                            <v-text-field v-model="meeting.name" label="Title *" outlined dense
                                :rules="[rules.required]" placeholder="Enter meeting title..."
                                prepend-inner-icon="mdi-format-title">
                            </v-text-field>
                        </v-col>

                        <!-- Date -->
                        <v-col cols="12" md="4">
                            <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition"
                                offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field :value="meeting.meeting_date ? $date(meeting.meeting_date).format('dddd, D MMM YYYY') : ''"
                                        label="Meeting Date *" hint="The day the meeting takes place" persistent-hint
                                        prepend-inner-icon="mdi-calendar" readonly outlined dense
                                        :rules="[() => rules.required(meeting.meeting_date)]" v-bind="attrs" v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="meeting.meeting_date" @input="dateMenu = false">
                                </v-date-picker>
                            </v-menu>
                        </v-col>

                        <!-- Start Time -->
                        <v-col cols="12" md="4">
                            <v-menu ref="startTimeMenu" v-model="startTimeMenu" :close-on-content-click="false"
                                :nudge-right="40" transition="scale-transition" offset-y max-width="290px"
                                min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field :value="$tz.showTime(meeting.start_time)" label="Start Time"
                                        prepend-inner-icon="mdi-clock-start" readonly outlined dense
                                        v-bind="attrs" v-on="on">
                                    </v-text-field>
                                </template>
                                <v-time-picker v-if="startTimeMenu" v-model="meeting.start_time" full-width :format="$tz.pickerFormat"
                                    @click:minute="$refs.startTimeMenu.save(meeting.start_time)">
                                </v-time-picker>
                            </v-menu>
                            <div v-if="meeting.start_time" class="text-caption grey--text">
                                {{ $tz.showTime($tz.joinToBaseline(meeting.meeting_date, meeting.start_time).time) }} company time
                            </div>
                        </v-col>

                        <!-- End Time -->
                        <v-col cols="12" md="4">
                            <v-menu ref="endTimeMenu" v-model="endTimeMenu" :close-on-content-click="false"
                                :nudge-right="40" transition="scale-transition" offset-y max-width="290px"
                                min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field :value="$tz.showTime(meeting.end_time)" label="End Time"
                                        prepend-inner-icon="mdi-clock-end" readonly outlined dense
                                        :rules="[() => rules.endTimeAfterStart(meeting.end_time)]"
                                        v-bind="attrs" v-on="on">
                                    </v-text-field>
                                </template>
                                <v-time-picker v-if="endTimeMenu" v-model="meeting.end_time" full-width :format="$tz.pickerFormat"
                                    @click:minute="$refs.endTimeMenu.save(meeting.end_time)">
                                </v-time-picker>
                            </v-menu>
                        </v-col>

                        <!-- Attendees -->
                        <v-col cols="12">
                            <v-autocomplete v-model="meeting.attendees" :items="users" item-text="name" item-value="id"
                                label="Attendees" outlined dense prepend-inner-icon="mdi-account-multiple"
                                hint="Select who should attend this meeting" persistent-hint clearable multiple chips
                                small-chips deletable-chips>
                                <template v-slot:selection="{ item }">
                                    <v-chip small :color="getAvatarColor(item.name)" dark close
                                        @click:close="removeAttendee(item.id)">
                                        <v-avatar left>
                                            <span class="white--text">{{ getInitials(item.name) }}</span>
                                        </v-avatar>
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar :color="getAvatarColor(item.name)">
                                        <span class="white--text">{{ getInitials(item.name) }}</span>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.email || 'No email' }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Company -->
                        <v-col cols="12" md="6">
                            <v-autocomplete v-model="meeting.company_id" :items="companies" item-text="name"
                                item-value="id" label="Company *" outlined dense :rules="[rules.required]"
                                prepend-inner-icon="mdi-domain" placeholder="Select company...">
                                <template v-slot:selection="{ item }">
                                    <v-chip small color="info" text-color="white">
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Meeting Type -->
                        <v-col cols="12" md="6">
                            <v-select v-model="meeting.frequency" :items="frequencyOptions" item-text="text"
                                item-value="value" label="Meeting Type *" outlined dense :rules="[rules.required]"
                                prepend-inner-icon="mdi-repeat">
                                <template v-slot:selection="{ item }">
                                    <v-chip small :color="getMeetingFrequencyColor(item.value)" text-color="white">
                                        {{ item.text }}
                                    </v-chip>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Recurrence Pattern (shown only when frequency is recurring) -->
                        <v-col cols="12" v-if="meeting.frequency === 'recurring'">
                            <v-card outlined class="pa-4">
                                <v-card-title class="text-subtitle-1 pa-0 mb-3">
                                    <v-icon class="mr-2" color="primary">mdi-repeat</v-icon>
                                    Recurrence Pattern
                                </v-card-title>

                                <v-row>
                                    <!-- Every X weeks -->
                                    <v-col cols="12" md="4">
                                        <div class="d-flex align-center">
                                            <span class="text-body-2 mr-2">Every</span>
                                            <v-text-field
                                                v-model.number="meeting.recurrence_interval"
                                                type="number"
                                                min="1"
                                                max="52"
                                                outlined
                                                dense
                                                hide-details
                                                style="max-width: 80px;"
                                                :rules="[rules.required, rules.positiveNumber]"
                                            ></v-text-field>
                                            <span class="text-body-2 ml-2">week(s)</span>
                                        </div>
                                    </v-col>

                                    <!-- Days of Week -->
                                    <v-col cols="12" md="8">
                                        <div class="text-body-2 mb-2">On these days:</div>
                                        <div class="d-flex flex-wrap" style="gap: 8px;">
                                            <v-chip
                                                v-for="day in daysOfWeek"
                                                :key="day.value"
                                                :color="meeting.recurrence_days.includes(day.value) ? 'primary' : 'default'"
                                                :text-color="meeting.recurrence_days.includes(day.value) ? 'white' : 'black'"
                                                small
                                                clickable
                                                @click="toggleRecurrenceDay(day.value)"
                                            >
                                                {{ day.label }}
                                            </v-chip>
                                        </div>
                                    </v-col>

                                    <!-- Series Start Date -->
                                    <v-col cols="12" md="6">
                                        <v-menu
                                            v-model="seriesStartDateMenu"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="meeting.series_start_date"
                                                    label="Series Start Date"
                                                    prepend-inner-icon="mdi-calendar"
                                                    readonly
                                                    outlined
                                                    dense
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :rules="[rules.required]"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="meeting.series_start_date"
                                                @input="seriesStartDateMenu = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>

                                    <!-- Series End Date (Optional) -->
                                    <v-col cols="12" md="6">
                                        <v-menu
                                            v-model="seriesEndDateMenu"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="meeting.series_end_date"
                                                    label="Series End Date (Optional)"
                                                    prepend-inner-icon="mdi-calendar"
                                                    readonly
                                                    outlined
                                                    dense
                                                    clearable
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :rules="[rules.endDateAfterStart]"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="meeting.series_end_date"
                                                :min="meeting.series_start_date"
                                                @input="seriesEndDateMenu = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>

                        <!-- Status -->
                        <v-col cols="12" md="6">
                            <v-select v-model="meeting.meeting_status_id" :items="meetingStatuses" item-text="name"
                                item-value="id" label="Status" outlined dense prepend-inner-icon="mdi-flag">
                                <template #selection="{ item }">
                                    <v-chip small :color="item.color" text-color="white">
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                                <template #item="{ item }">
                                    <div class="d-flex align-center">
                                        <v-icon :color="item.color" class="mr-2">mdi-circle</v-icon>
                                        {{ item.name }}
                                    </div>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Memo -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model="meeting.memo_url" label="Memo" outlined dense
                                prepend-inner-icon="mdi-link" placeholder="Link to Google Docs...">
                            </v-text-field>
                        </v-col>

                        <!-- Link -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model="meeting.conference_link" label="Link" outlined dense
                                prepend-inner-icon="mdi-video" placeholder="Enter video conference link..."
                                hint="Enter the Google Meet or conference URL (e.g. https://meet.google.com/xxx-xxxx-xxx)" persistent-hint
                                :rules="[rules.url]">
                            </v-text-field>
                        </v-col>

                        <!-- Agenda -->
                        <v-col cols="12">
                            <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                                <v-icon class="mr-1" size="20">mdi-text-box-outline</v-icon>
                                Agenda
                            </label>
                            <wysiwyg v-model="meeting.agenda" />
                        </v-col>

                        <!-- Announcement to Attendees -->
                        <v-col cols="12">
                            <v-textarea v-model="meeting.announcement" label="Announcement to Attendees" outlined
                                rows="3" prepend-inner-icon="mdi-bullhorn"
                                placeholder="Enter announcement message for attendees..." counter="500"
                                :rules="[rules.maxLength(500)]">
                            </v-textarea>
                        </v-col>

                        <!-- Attachments -->
                        <!-- <v-col cols="12">
                            <v-autocomplete v-model="meeting.attachments" :items="resources" item-text="text"
                                item-value="value" label="Attachments" outlined dense multiple chips deletable-chips
                                prepend-inner-icon="mdi-paperclip" placeholder="Select attachments..."
                                :loading="resourcesLoading">
                                <template v-slot:selection="{ item, index }">
                                    <v-chip small close @click:close="removeAttachment(index)" color="accent"
                                        text-color="white">
                                        <v-icon left size="16">{{ getFileIcon(item.file_type) }}</v-icon>
                                        {{ item.text }}
                                    </v-chip>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar>
                                        <v-icon :color="getFileTypeColor(item.file_type)">
                                            {{ getFileIcon(item.file_type) }}
                                        </v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{ item.subtitle }} • {{ item.size }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col> -->
                    </v-row>
                </v-form>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="pa-6">
                <v-spacer></v-spacer>
                <v-btn @click="cancel" text large class="mr-2">
                    Cancel
                </v-btn>
                <v-btn @click="updateMeeting" color="primary" large :disabled="updating" :loading="updating">
                    <v-icon left>mdi-content-save</v-icon>
                    Update Meeting
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "EditMeetingDialog",
    props: {
        value: {
            type: Boolean,
            default: false
        },
        meetingData: {
            type: Object,
            default: () => ({})
        },
        users: {
            type: Array,
            default: () => []
        },
        companies: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            updating: false,
            formValid: false,
            dateMenu: false,
            startTimeMenu: false,
            endTimeMenu: false,
            seriesStartDateMenu: false,
            seriesEndDateMenu: false,
            meeting: {
                id: null,
                name: null,
                company_id: null,
                agenda: null,
                frequency: 'once',
                meeting_status_id: 1,
                creator_id: null,
                meeting_date: null,
                start_time: null,
                end_time: null,
                memo_url: null,
                conference_link: null,
                announcement: null,
                attendees: [],
                attachments: [],
                // Recurrence fields
                recurrence_interval: 1,
                recurrence_days: [],
                series_start_date: null,
                series_end_date: null
            },
            rules: {
                required: value => !!value || 'This field is required',
                url: value => {
                    if (!value) return true; // Optional field
                    const pattern = /^(https?:\/\/)[^\s]+$/i;
                    return pattern.test(value) || 'Please enter a valid URL (must start with https:// or http://)';
                },
                endTimeAfterStart: value => {
                    if (!value || !this.meeting.start_time) return true;
                    return this.meeting.start_time < value || 'End time must be after start time';
                },
                endDateAfterStart: value => {
                    if (!value || !this.meeting.series_start_date) return true;
                    return this.meeting.series_start_date <= value || 'End date must be after start date';
                },
                positiveNumber: value => {
                    if (!value) return true;
                    return value > 0 || 'Must be a positive number';
                },
                maxLength: (length) => value => {
                    if (!value) return true;
                    return value.length <= length || `Maximum ${length} characters allowed`;
                }
            },
            daysOfWeek: [
                { label: 'Mon', value: 1 },
                { label: 'Tue', value: 2 },
                { label: 'Wed', value: 3 },
                { label: 'Thu', value: 4 },
                { label: 'Fri', value: 5 },
                { label: 'Sat', value: 6 },
                { label: 'Sun', value: 0 }
            ]
        };
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_meeting_statuses: "meetings/get_meeting_statuses",
            get_resources: "resources/get_resources_for_select",
            resources_loading: "resources/is_loading"
        }),

        dialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },

        meetingStatuses() {
            return this.get_meeting_statuses || [];
        },

        frequencyOptions() {
            return [
                { text: 'Once', value: 'once' },
                { text: 'Recurring', value: 'recurring' }
            ];
        },

        resources() {
            return this.get_resources || [];
        },

        resourcesLoading() {
            return this.resources_loading;
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.initializeForm();
            }
        },
        meetingData: {
            deep: true,
            handler() {
                if (this.value) {
                    this.initializeForm();
                }
            }
        }
    },
    methods: {
        initializeForm() {
            // Pre-populate with existing meeting data
            // Extract attendee IDs from access array
            const attendeeIds = this.meetingData.access?.map(access => access.user_id) || [];
            
            const baseDate = this.formatDateValue(this.meetingData.meeting_date)
                || this.formatDateFromCreated(this.meetingData.created_at);
            // Date and time MUST be converted together — a zone shift can roll the date.
            const shownStart = this.$tz.splitLocalDateTime(baseDate, this.meetingData.start_time);
            const shownEnd = this.$tz.splitLocalDateTime(baseDate, this.meetingData.end_time);

            this.meeting = {
                id: this.meetingData.id || null,
                name: this.meetingData.name || null,
                company_id: this.meetingData.company_id || null,
                agenda: this.meetingData.agenda || null,
                frequency: this.meetingData.frequency || 'once',
                meeting_status_id: this.meetingData.meeting_status_id || this.get_meeting_statuses?.[0]?.id || 1,
                creator_id: this.meetingData.creator_id || this.meetingData.user_id || null,
                meeting_date: shownStart.date || baseDate,
                start_time: shownStart.time || null,
                end_time: shownEnd.time || null,
                memo_url: this.meetingData.memo_url || null,
                conference_link: this.meetingData.conference_link || null,
                announcement: this.meetingData.announcement || null,
                attendees: attendeeIds,
                attachments: this.meetingData.attachments || [],
                // Recurrence fields
                recurrence_interval: this.meetingData.recurrence_interval || 1,
                recurrence_days: this.meetingData.recurrence_days || [],
                series_start_date: this.formatDateValue(this.meetingData.series_start_date) || null,
                series_end_date: this.formatDateValue(this.meetingData.series_end_date) || null
            };

            this.$nextTick(() => {
                this.$refs.editMeetingForm?.resetValidation();
            });
        },

        formatDateFromCreated(createdAt) {
            if (!createdAt) return this.$date().format('YYYY-MM-DD');
            return this.$date(createdAt).format('YYYY-MM-DD');
        },

        formatDateValue(dateValue) {
            if (!dateValue) return null;
            
            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            
            // Otherwise, parse with dayjs and format
            const parsedDate = this.$date(dateValue);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        },

        async updateMeeting() {
            if (!this.$refs.editMeetingForm.validate()) return;

            this.updating = true;
            try {
                // eslint-disable-next-line no-unused-vars
                const { creator_id, ...rest } = this.meeting;

                // Convert back to the company baseline zone so the stored value is
                // unchanged regardless of the editor's display zone.
                const savedStart = this.$tz.joinToBaseline(this.meeting.meeting_date, this.meeting.start_time);
                const savedEnd = this.$tz.joinToBaseline(this.meeting.meeting_date, this.meeting.end_time);

                const payload = {
                    ...rest,
                    meeting_date: savedStart.date || this.meeting.meeting_date,
                    start_time: savedStart.time,
                    end_time: savedEnd.time,
                };

                this.$emit('update-meeting', payload);
                this.cancel();
            } catch (error) {
                console.error('Error in edit meeting dialog:', error);
            } finally {
                this.updating = false;
            }
        },

        cancel() {
            this.dialog = false;
        },

        removeAttachment(index) {
            this.meeting.attachments.splice(index, 1);
        },

        removeAttendee(userId) {
            const index = this.meeting.attendees.indexOf(userId);
            if (index > -1) {
                this.meeting.attendees.splice(index, 1);
            }
        },

        toggleRecurrenceDay(dayValue) {
            const index = this.meeting.recurrence_days.indexOf(dayValue);
            if (index > -1) {
                this.meeting.recurrence_days.splice(index, 1);
            } else {
                this.meeting.recurrence_days.push(dayValue);
            }
        },

        // Helper methods
        getMeetingFrequencyColor(frequency) {
            switch (frequency) {
                case 'once':
                    return 'primary';
                case 'recurring':
                    return 'success';
                default:
                    return 'grey';
            }
        },

        getAvatarColor(name) {
            const colors = [
                'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info',
                'purple', 'pink', 'indigo', 'cyan', 'teal', 'green', 'orange', 'brown'
            ];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },

        getInitials(name) {
            if (!name) return 'U';
            return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        },

        getFileIcon(fileType) {
            const icons = {
                'pdf': 'mdi-file-pdf-box',
                'docx': 'mdi-file-word-box',
                'doc': 'mdi-file-word-box',
                'xlsx': 'mdi-file-excel-box',
                'xls': 'mdi-file-excel-box',
                'pptx': 'mdi-file-powerpoint-box',
                'ppt': 'mdi-file-powerpoint-box',
                'txt': 'mdi-file-document-outline',
                'default': 'mdi-file-outline'
            };
            return icons[fileType] || icons.default;
        },

        getFileTypeColor(fileType) {
            const colors = {
                'pdf': 'red',
                'docx': 'blue',
                'doc': 'blue',
                'xlsx': 'green',
                'xls': 'green',
                'pptx': 'orange',
                'ppt': 'orange',
                'txt': 'grey',
                'default': 'grey'
            };
            return colors[fileType] || colors.default;
        }
    }
};
</script>

<style lang="scss" scoped>
.v-card {
    border-radius: 12px;

    .v-card-title {
        background-color: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
    }

    .v-card-text {
        max-height: 70vh;
        overflow-y: auto;
    }
}

// Form styling
.v-form {

    .v-text-field,
    .v-select,
    .v-autocomplete,
    .v-textarea {
        .v-input__control {
            .v-input__slot {
                border-radius: 8px;
            }
        }
    }
}

// Time picker styling
.v-time-picker {
    border-radius: 8px;
}

// Chip styling
.v-chip {
    border-radius: 16px;
    font-weight: 500;

    .v-avatar {
        font-size: 0.75rem;
        font-weight: 600;
    }
}

// WYSIWYG editor styling
::v-deep .wysiwyg-editor {
    border: 1px solid #ccc;
    border-radius: 8px;
    min-height: 120px;
}

// Responsive adjustments
@media (max-width: 768px) {
    .v-dialog {
        .v-card {
            margin: 12px;
            max-width: calc(100% - 24px) !important;

            .v-card-text {
                max-height: 60vh;
            }
        }
    }
}
</style>